import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "design-system/molecules/Table"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { CONSTANTS } from "lib/constants"
import { getErrorTranslations } from "lib/i18n"
import { GripVertical, Plus, Trash } from "lucide-react"
import {
  ConvertSizeChartPayload,
  FrontendConvertSizeChartSchema,
  useShopifyConvertSizeChartMutation,
} from "modules/settings/hooks/shopify/useShopifyConvertSizeChartMutation"
import { Loading } from "modules/shared/components/Loading"
import { ChangeEvent, DragEvent, KeyboardEvent, useEffect, useId, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { ConvertSizeChartResponseData } from "schema"
import { z } from "zod"
import { getInchesValue } from "./sizeChartConversion"

type ConvertSizeChartResult = z.infer<typeof ConvertSizeChartResponseData>

type Props = {
  isOpen: boolean
  result: ConvertSizeChartResult | null
  onConverted: (
    result: ConvertSizeChartResult | null,
    options?: { resetGeneratedImage?: boolean },
  ) => void
}

const ACCEPTED_SIZE_CHART_FILE_TYPES = [
  ".xls",
  ".xlsx",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/*",
  "application/pdf",
].join(",")

const SIZE_CHART_TABLE_INPUT_CLASS_NAME =
  "h-10 w-full min-w-0 bg-transparent px-3 py-2 text-sm outline-none " +
  "focus-visible:ring-2 focus-visible:ring-ring"
const SIZE_CHART_TABLE_HEADER_INPUT_CLASS_NAME = `${SIZE_CHART_TABLE_INPUT_CLASS_NAME} font-semibold`
const SIZE_CHART_TABLE_SIZE_INPUT_CLASS_NAME = `${SIZE_CHART_TABLE_INPUT_CLASS_NAME} font-medium`
const SIZE_CHART_TABLE_VALUE_INPUT_CLASS_NAME =
  "h-7 w-full min-w-0 bg-transparent text-sm outline-none " +
  "focus-visible:ring-2 focus-visible:ring-ring"
const SIZE_CHART_TABLE_DELETE_COLUMN_BUTTON_CLASS_NAME =
  "pointer-events-none opacity-0 transition-opacity group-hover:pointer-events-auto " +
  "group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100"
const SIZE_CHART_TABLE_MOVE_COLUMN_BUTTON_CLASS_NAME =
  "cursor-grab text-muted-foreground opacity-70 active:cursor-grabbing hover:opacity-100"
const SIZE_CHART_TABLE_WRAPPER_CLASS_NAME =
  "mt-2 min-w-0 max-w-full overflow-hidden rounded-md border " +
  "[&_[data-slot=table-container]]:max-h-[450px] " +
  "[&_[data-slot=table-container]]:overflow-auto"

export function ConvertSizeChartConvertForm({ isOpen, result, onConverted }: Props) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const form = useForm<ConvertSizeChartPayload>({
    resolver: zodResolver(FrontendConvertSizeChartSchema),
  })
  const { mutateAsync: convertSizeChart, isPending: isConverting } =
    useShopifyConvertSizeChartMutation()

  useEffect(() => {
    if (!isOpen) {
      form.reset()
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }, [form, isOpen])

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const document = e.target.files
    if (!document || document.length === 0) return

    form.setValue("document", document, { shouldValidate: true, shouldDirty: true })
  }

  const onSubmit = (data: ConvertSizeChartPayload) => {
    convertSizeChart(data)
      .then((convertedResult) => onConverted(convertedResult, { resetGeneratedImage: true }))
      .catch(() => onConverted(null, { resetGeneratedImage: true }))
  }

  return (
    <Form
      {...form}
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex min-w-0 flex-col gap-4"
      errorTranslations={getErrorTranslations()}
    >
      <div className="flex flex-col w-full gap-4">
        <FormField
          control={form.control}
          name="document"
          render={() => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.shopify.convertSizeChart.form.image")}
              </FormLabel>
              <FormControl>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_SIZE_CHART_FILE_TYPES}
                  onChange={handleFileChange}
                  className={
                    "block w-full cursor-pointer text-sm text-muted-foreground " +
                    "file:mr-4 file:rounded-md file:border-0 file:bg-muted " +
                    "file:px-4 file:py-2 file:text-sm file:font-medium " +
                    "file:text-foreground hover:file:bg-muted/80"
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div>
        <Button disabled={isConverting || !form.formState.isValid}>
          {t("settings.integrations.shopify.convertSizeChart.form.convert")}
        </Button>
      </div>
      <div>
        {isConverting && <Loading />}
        {!isConverting && result && (
          <SizeChartResultTable
            result={result}
            onChange={onConverted}
            sizeLabel={t("settings.integrations.shopify.convertSizeChart.form.size")}
            columnLabel={t("settings.integrations.shopify.convertSizeChart.form.column")}
            moveColumnLabel={t("settings.integrations.shopify.convertSizeChart.form.moveColumn")}
            deleteColumnLabel={t(
              "settings.integrations.shopify.convertSizeChart.form.deleteColumn",
            )}
            deleteColumnTitle={t(
              "settings.integrations.shopify.convertSizeChart.form.deleteColumnTitle",
            )}
            convertToInchesLabel={t(
              "settings.integrations.shopify.convertSizeChart.form.convertToInches",
            )}
            addRowLabel={t("settings.integrations.shopify.convertSizeChart.form.addRow")}
            deleteRowLabel={t("settings.integrations.shopify.convertSizeChart.form.deleteRow")}
            deleteRowTitle={t("settings.integrations.shopify.convertSizeChart.form.deleteRowTitle")}
            deleteRowDescription={t(
              "settings.integrations.shopify.convertSizeChart.form.deleteRowDescription",
            )}
            getDeleteColumnDescription={(column) =>
              t("settings.integrations.shopify.convertSizeChart.form.deleteColumnDescription", {
                column,
              })
            }
            cancelLabel={t("shared.cancel")}
            deleteLabel={t("shared.delete")}
          />
        )}
      </div>
    </Form>
  )
}

type SizeChartResultTableProps = {
  result: ConvertSizeChartResult
  onChange: (result: ConvertSizeChartResult) => void
  sizeLabel: string
  columnLabel: string
  moveColumnLabel: string
  deleteColumnLabel: string
  deleteColumnTitle: string
  convertToInchesLabel: string
  addRowLabel: string
  deleteRowLabel: string
  deleteRowTitle: string
  deleteRowDescription: string
  getDeleteColumnDescription: (column: string) => string
  cancelLabel: string
  deleteLabel: string
}

type SizeChartDeleteTarget = { type: "column"; index: number } | { type: "row"; index: number }

function SizeChartResultTable({
  result,
  onChange,
  sizeLabel,
  columnLabel,
  moveColumnLabel,
  deleteColumnLabel,
  deleteColumnTitle,
  convertToInchesLabel,
  addRowLabel,
  deleteRowLabel,
  deleteRowTitle,
  deleteRowDescription,
  getDeleteColumnDescription,
  cancelLabel,
  deleteLabel,
}: SizeChartResultTableProps) {
  const convertToInchesId = useId()
  const [deleteTarget, setDeleteTarget] = useState<SizeChartDeleteTarget | null>(null)
  const [draggedColumnIndex, setDraggedColumnIndex] = useState<number | null>(null)
  const [dragOverColumnIndex, setDragOverColumnIndex] = useState<number | null>(null)
  const columnToDelete =
    deleteTarget?.type === "column" ? result.result.columns[deleteTarget.index] : null
  const deleteDialogTitle = deleteTarget?.type === "row" ? deleteRowTitle : deleteColumnTitle
  const deleteDialogDescription = (() => {
    if (deleteTarget?.type === "row") return deleteRowDescription
    if (columnToDelete) return getDeleteColumnDescription(columnToDelete)
    return null
  })()

  const updateConvertToInches = (checked: boolean | "indeterminate") => {
    onChange({
      ...result,
      result: {
        ...result.result,
        convertToInches: checked === true,
      },
    })
  }

  const updateColumn = (columnIndex: number, value: string) => {
    onChange({
      ...result,
      result: {
        ...result.result,
        columns: result.result.columns.map((column, index) =>
          index === columnIndex ? value : column,
        ),
      },
    })
  }

  const updateSizeLabel = (sizeIndex: number, value: string) => {
    onChange({
      ...result,
      result: {
        ...result.result,
        sizes: result.result.sizes.map((size, index) =>
          index === sizeIndex ? { ...size, label: value } : size,
        ),
      },
    })
  }

  const updateSizeValue = (sizeIndex: number, valueIndex: number, value: string) => {
    onChange({
      ...result,
      result: {
        ...result.result,
        sizes: result.result.sizes.map((size, index) => {
          if (index !== sizeIndex) return size

          return {
            ...size,
            values: Array.from(
              { length: Math.max(size.values.length, valueIndex + 1) },
              (_, currentValueIndex) =>
                currentValueIndex === valueIndex ? value : (size.values[currentValueIndex] ?? ""),
            ),
          }
        }),
      },
    })
  }

  const addRow = () => {
    onChange({
      ...result,
      result: {
        ...result.result,
        sizes: [
          ...result.result.sizes,
          { label: "", values: Array.from({ length: result.result.columns.length }, () => "") },
        ],
      },
    })
  }

  const reorderValues = (values: string[], fromIndex: number, toIndex: number) => {
    const nextValues = Array.from(
      { length: result.result.columns.length },
      (_, index) => values[index] ?? "",
    )
    const [movedValue] = nextValues.splice(fromIndex, 1)

    nextValues.splice(toIndex, 0, movedValue ?? "")

    return nextValues
  }

  const moveColumn = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return

    const nextColumns = [...result.result.columns]
    const [movedColumn] = nextColumns.splice(fromIndex, 1)

    if (!movedColumn) return

    nextColumns.splice(toIndex, 0, movedColumn)

    onChange({
      ...result,
      result: {
        ...result.result,
        columns: nextColumns,
        sizes: result.result.sizes.map((size) => ({
          ...size,
          values: reorderValues(size.values, fromIndex, toIndex),
        })),
      },
    })
  }

  const handleColumnDragStart = (event: DragEvent<HTMLButtonElement>, columnIndex: number) => {
    setDraggedColumnIndex(columnIndex)
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", columnIndex.toString())

    const dragImage = document.createElement("div")
    dragImage.className =
      "rounded-md border bg-background px-3 py-2 text-sm font-medium shadow-lg opacity-90"
    dragImage.style.position = "fixed"
    dragImage.style.top = "-1000px"
    dragImage.style.left = "-1000px"
    const columnName = result.result.columns[columnIndex] ?? ""

    const columnValues = result.result.sizes
      .map((size) => size.values[columnIndex])
      .filter(Boolean)
      .slice(0, 4)
      .join(" / ")

    dragImage.textContent = columnValues ? `${columnName} / ${columnValues}` : columnName
    document.body.appendChild(dragImage)
    event.dataTransfer.setDragImage(dragImage, 16, 16)
    window.setTimeout(() => dragImage.remove(), 0)
  }

  const handleColumnDragOver = (event: DragEvent<HTMLTableCellElement>, columnIndex: number) => {
    if (draggedColumnIndex === null) return

    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    setDragOverColumnIndex(columnIndex)
  }

  const handleColumnDrop = (event: DragEvent<HTMLTableCellElement>, columnIndex: number) => {
    event.preventDefault()

    if (draggedColumnIndex !== null) {
      moveColumn(draggedColumnIndex, columnIndex)
    }

    setDraggedColumnIndex(null)
    setDragOverColumnIndex(null)
  }

  const handleColumnDragEnd = () => {
    setDraggedColumnIndex(null)
    setDragOverColumnIndex(null)
  }

  const confirmDelete = () => {
    if (!deleteTarget) return

    if (deleteTarget.type === "column") {
      onChange({
        ...result,
        result: {
          ...result.result,
          columns: result.result.columns.filter((_, index) => index !== deleteTarget.index),
          sizes: result.result.sizes.map((size) => ({
            ...size,
            values: size.values.filter((_, index) => index !== deleteTarget.index),
          })),
        },
      })
    } else {
      onChange({
        ...result,
        result: {
          ...result.result,
          sizes: result.result.sizes.filter((_, index) => index !== deleteTarget.index),
        },
      })
    }

    setDeleteTarget(null)
  }

  const preventEnterSubmit = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
    }
  }

  return (
    <>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id={convertToInchesId}
            checked={result.result.convertToInches}
            onCheckedChange={updateConvertToInches}
          />
          <label htmlFor={convertToInchesId} className="text-sm font-medium">
            {convertToInchesLabel}
          </label>
        </div>
        <Button type="button" variant="outline" onClick={addRow}>
          <Plus className="size-4" />
          <span>{addRowLabel}</span>
        </Button>
      </div>
      <div className={SIZE_CHART_TABLE_WRAPPER_CLASS_NAME}>
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow>
              <TableHead noPadding className="min-w-24">
                <span className="block px-3 py-2 text-sm font-semibold">{sizeLabel}</span>
              </TableHead>
              {result.result.columns.map((column, columnIndex) => (
                <TableHead
                  key={columnIndex}
                  noPadding
                  onDragOver={(event) => handleColumnDragOver(event, columnIndex)}
                  onDrop={(event) => handleColumnDrop(event, columnIndex)}
                  className={
                    "group min-w-44 pr-2 transition-colors [&>div>span]:w-full " +
                    (dragOverColumnIndex === columnIndex ? "bg-muted " : "") +
                    (draggedColumnIndex === columnIndex ? "bg-muted/60 opacity-50" : "")
                  }
                >
                  <div className="flex items-center">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      draggable
                      onDragStart={(event) => handleColumnDragStart(event, columnIndex)}
                      onDragEnd={handleColumnDragEnd}
                      aria-label={moveColumnLabel}
                      title={moveColumnLabel}
                      className={SIZE_CHART_TABLE_MOVE_COLUMN_BUTTON_CLASS_NAME}
                    >
                      <GripVertical />
                    </Button>
                    <input
                      accept={CONSTANTS.acceptedImageTypes.join(",")}
                      value={column}
                      onChange={(event) => updateColumn(columnIndex, event.target.value)}
                      onKeyDown={preventEnterSubmit}
                      aria-label={columnLabel}
                      className={SIZE_CHART_TABLE_HEADER_INPUT_CLASS_NAME}
                    />
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => setDeleteTarget({ type: "column", index: columnIndex })}
                    aria-label={deleteColumnLabel}
                    title={deleteColumnLabel}
                    className={SIZE_CHART_TABLE_DELETE_COLUMN_BUTTON_CLASS_NAME}
                  >
                    <Trash />
                  </Button>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.result.sizes.map((size, sizeIndex) => (
              <TableRow key={sizeIndex}>
                <TableCell noPadding>
                  <div className="group flex items-center pr-2">
                    <input
                      value={size.label}
                      onChange={(event) => updateSizeLabel(sizeIndex, event.target.value)}
                      onKeyDown={preventEnterSubmit}
                      aria-label={sizeLabel}
                      className={SIZE_CHART_TABLE_SIZE_INPUT_CLASS_NAME}
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => setDeleteTarget({ type: "row", index: sizeIndex })}
                      aria-label={deleteRowLabel}
                      title={deleteRowLabel}
                      className={SIZE_CHART_TABLE_DELETE_COLUMN_BUTTON_CLASS_NAME}
                    >
                      <Trash />
                    </Button>
                  </div>
                </TableCell>
                {result.result.columns.map((column, valueIndex) => (
                  <TableCell
                    key={valueIndex}
                    noPadding
                    className={
                      draggedColumnIndex === valueIndex ? "bg-muted/60 opacity-50" : undefined
                    }
                  >
                    <SizeChartValueCell
                      column={column}
                      convertToInches={result.result.convertToInches}
                      onChange={(value) => updateSizeValue(sizeIndex, valueIndex, value)}
                      onKeyDown={preventEnterSubmit}
                      sizeLabel={size.label}
                      value={size.values[valueIndex] ?? ""}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={deleteTarget !== null} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{deleteDialogTitle}</DialogTitle>
            {deleteDialogDescription && (
              <DialogDescription>{deleteDialogDescription}</DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
              {cancelLabel}
            </Button>
            <Button type="button" variant="destructive" onClick={confirmDelete}>
              {deleteLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

type SizeChartValueCellProps = {
  column: string
  convertToInches: boolean
  onChange: (value: string) => void
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
  sizeLabel: string
  value: string
}

function SizeChartValueCell({
  column,
  convertToInches,
  onChange,
  onKeyDown,
  sizeLabel,
  value,
}: SizeChartValueCellProps) {
  const inchesValue = convertToInches ? getInchesValue(value) : null

  return (
    <div className="flex min-h-10 flex-col justify-center px-3 py-2">
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        aria-label={`${sizeLabel} ${column}`}
        className={SIZE_CHART_TABLE_VALUE_INPUT_CLASS_NAME}
      />
      {inchesValue !== null && (
        <span className="text-xs leading-tight text-muted-foreground">{inchesValue}</span>
      )}
    </div>
  )
}
